import type { ChartFormat, CollectionViewDTO } from '@next-space/fe-api-idl';
import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import { getState } from 'src/redux/store';
import * as reducers from 'src/redux/reducers';
import type { LocalUser } from 'src/redux/reducers/users';
import type { NextBlock } from 'src/redux/types';
import { Images } from 'src/image';
import i18n from '@flowus/common/i18n';

interface ChartType {
  name: string;
  info: {
    tip: string;
    chartMode: ChartFormat['chartMode'];
    image: string;
  }[];
}
export const chartTypeData: ChartType[] = [
  {
    name: i18n.t('editor.component.chart.chart_config_panel.data.number_stats'),
    info: [
      {
        image: Images.chartNumber,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.number_stats'),
        chartMode: {
          type: 'Number',
          display: 'base',
        },
      },
    ],
  },
  {
    name: i18n.t('editor.component.chart.chart_config_panel.data.bar_chart'),
    info: [
      {
        image: Images.chartBarBase,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.base_bar_chart'),
        chartMode: {
          type: 'Bar',
          display: 'base',
        },
      },
      {
        image: Images.chartBarStack,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.stack_bar_chart'),
        chartMode: {
          type: 'Bar',
          display: 'stack',
        },
      },
      {
        image: Images.chartBarPercent,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.percent_stack_bar_chart'),
        chartMode: {
          type: 'Bar',
          display: 'percent',
        },
      },
    ],
  },
  {
    name: i18n.t('editor.component.chart.chart_config_panel.data.line_chart'),
    info: [
      {
        image: Images.chartLineBase,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.base_line_chart'),
        chartMode: {
          type: 'Line',
          display: 'base',
        },
      },
      {
        image: Images.chartLineSmooth,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.smooth_line_chart'),
        chartMode: {
          type: 'Line',
          display: 'smooth',
        },
      },
      {
        image: Images.chartLineStep,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.step_line_chart'),
        chartMode: {
          type: 'Line',
          display: 'step',
        },
      },
    ],
  },
  {
    name: i18n.t('editor.component.chart.chart_config_panel.data.bar_chart'),
    info: [
      {
        image: Images.chartHorizontalbarBase,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.base_horizontal_bar_chart'),
        chartMode: {
          type: 'HorizontalBar',
          display: 'base',
        },
      },
      {
        image: Images.chartHorizontalbarStack,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.stack_horizontal_bar_chart'),
        chartMode: {
          type: 'HorizontalBar',
          display: 'stack',
        },
      },
      {
        image: Images.chartHorizontalbarPercent,
        tip: i18n.t(
          'editor.component.chart.chart_config_panel.data.percent_stack_horizontal_bar_chart'
        ),
        chartMode: {
          type: 'HorizontalBar',
          display: 'percent',
        },
      },
    ],
  },
  {
    name: i18n.t('editor.component.chart.chart_config_panel.data.pie_chart'),
    info: [
      {
        image: Images.chartPieBase,
        tip: i18n.t('editor.component.chart.chart_config_panel.data.pie_chart'),
        chartMode: {
          type: 'Pie',
          display: 'base',
        },
      },
    ],
  },
];
/** 无效字段 */
export const invalidProperty = {
  type: undefined,
  title: i18n.t('editor.component.chart.chart_config_panel.data.invalid_field'),
  icon: undefined,
};

export const createPreviewStore = (chartId: string) => {
  const reducer = combineReducers(reducers);
  const collectionViews: Record<string, CollectionViewDTO> = {};
  const users: Record<string, LocalUser> = {};
  const blocks: Record<string, NextBlock> = {};

  const globalStore = getState();
  const chartBlock = globalStore.blocks[chartId];
  chartBlock && (blocks[chartId] = chartBlock);
  const collectionId = chartBlock?.data.format?.chartFormat?.ref?.uuid;
  Object.assign(users, globalStore.users);
  if (collectionId) {
    // 如果配置了多维表，就把多维表的数据塞进来
    const collection = globalStore.blocks[collectionId];
    if (collection) {
      blocks[collectionId] = collection;
      collection.subNodes.forEach((v) => {
        const sub = globalStore.blocks[v];
        sub && (blocks[v] = sub);
      });
    }
    let parent = globalStore.blocks[collection?.parentId ?? ''];
    // 权限相关需要用到
    while (parent) {
      blocks[parent.uuid] = parent;
      // @ts-ignore space check
      if (parent.type === 'SPACE') break;
      parent = globalStore.blocks[parent?.parentId ?? ''];
    }
    collection?.views?.forEach((v) => {
      const cv = globalStore.collectionViews[v];
      cv && (collectionViews[v] = cv);
    });
  }

  const store = configureStore({
    reducer,
    preloadedState: {
      blocks,
      users,
      collectionViews,
    },
  });
  return store;
};
